import { memo, useSyncExternalStore } from 'react';
import { clamp, priceY } from './chart-data';
import type { Anchor, Candle, Scale } from './chart-data';
import { timeframeLabel } from './zone-math';
import type { Timeframe } from './zone-math';
import { formatPrice } from './instruments';
import type { Symbol } from './instruments';

type Hover = { x: number; anchor: Anchor; candle: Candle } | null;



// Only the small cursor and OHLC components subscribe. Moving the mouse no
// longer renders the chart, toolbar, dot list or mapping calculation.
export function createCursor() {
  let value: Hover = null;
  const listeners = new Set<() => void>();
  return {
    read: () => value,
    candle: () => value?.candle,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    set: (next: Hover) => { if (value === next) return; value = next; listeners.forEach(listener => listener()); },
  };
}
type Cursor = ReturnType<typeof createCursor>;

export const Ohlc = memo(function Ohlc({ symbol, cursor, last, timeframe, emptyMessage }: { symbol: Symbol; cursor: Cursor; last?: Candle; timeframe: Timeframe; emptyMessage?: string }) {
  const fmt = (price: number) => formatPrice(symbol, price);
  const candle = useSyncExternalStore(cursor.subscribe, cursor.candle, cursor.candle) || last;
  return <div className="zp-ohlc"><b>{timeframeLabel(timeframe)}</b>{candle ? <>
    <span>O <strong>{fmt(candle.open)}</strong></span><span>H <strong>{fmt(candle.high)}</strong></span>
    <span>L <strong>{fmt(candle.low)}</strong></span><span>C <strong>{fmt(candle.close)}</strong></span>
  </> : <span>{emptyMessage || 'Loading market candles…'}</span>}</div>;
});

export const ChartCursor = memo(function ChartCursor({ symbol, cursor, scale, left, right, width }: { symbol: Symbol; cursor: Cursor; scale: Scale; left: number; right: number; width: number }) {
  const fmt = (price: number) => formatPrice(symbol, price);
  const hover = useSyncExternalStore(cursor.subscribe, cursor.read, cursor.read);
  if (!hover) return null;
  const y = clamp(priceY(hover.anchor.price, scale), scale.top, scale.bottom);
  const x = clamp(hover.x, left, right);
  return <g pointerEvents="none" data-testid="chart-cursor">
    <g opacity=".5"><line x1={x} x2={x} y1={scale.top} y2={scale.bottom} stroke="#5d6875" strokeDasharray="3 5" />
      <line x1={left} x2={right} y1={y} y2={y} stroke="#5d6875" strokeDasharray="3 5" /></g>
    <rect x={right + 2} y={y - 10} width={width - right - 4} height="20" rx="2" fill="#354250" />
    <text x={right + 8} y={y + 4} fill="#ffffff" fontSize="10">{fmt(hover.anchor.price)}</text>
  </g>;
});
